import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app';
import { CONSENT_CHANGED_EVENT } from '~~/common/config/OptanonCategoryConfig';
import type { AwLoggerClient } from 'awLoggerModule/runtime/shared/loggingTypes';
import type { AwMergedConfigType } from '~~/common/plugins/aw-runtime-config';

export type ScriptTagType = {
  properties: {
    src?: string
    defer?: boolean
    innerHTML?: string
    async?: boolean
  }
  attributes?: Record<string, string | boolean>
};

function createPluginProvides () {
  const nuxtApp = useNuxtApp();

  const {
    $awMergedConfig,
    $logger,
  }: {
    $awMergedConfig: AwMergedConfigType,
    $logger: AwLoggerClient
  } = nuxtApp;

  let scriptTags: ScriptTagType[] = [];

  const oneTrustScripts: ScriptTagType[] = [
    {
      properties: {
        src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
        defer: true,
      },
      attributes: {
        'data-document-language': true,
        'data-domain-script': $awMergedConfig.onetrustId,
      },
    },
    {
      properties: {
        // OneTrust calls OptanonWrapper on every consent change. Broadcast a
        // single consent event so every consent-gated script (SFMC, GAM, …) can
        // react independently — no script owns the callback, so each works even
        // when the others are disabled.
        innerHTML: `function OptanonWrapper () { window.dispatchEvent(new CustomEvent('${CONSENT_CHANGED_EVENT}')); }`,
      },
      attributes: {},
    },
  ];

  if ($awMergedConfig.onetrustEnabled && $awMergedConfig.onetrustId && !$awMergedConfig.onetrustDelayEnabled) {
    scriptTags = scriptTags.concat(oneTrustScripts);
  }

  if ($awMergedConfig.optinMonsterAccountId && $awMergedConfig.optinMonsterUserId) {
    scriptTags.push({
      properties: {
        innerHTML: `(function(d,u,ac){var s=d.createElement('script');s.src='https://a.omappapi.com/app/js/api.min.js';s.async=true;s.dataset.user=u;s.dataset.account=ac;d.head.appendChild(s);})(document,${$awMergedConfig.optinMonsterUserId},${$awMergedConfig.optinMonsterAccountId});`,
      },
      attributes: {},
    });
  }

  if ($awMergedConfig.chatbotEnabled && $awMergedConfig.chatbotId && $awMergedConfig.chatbotUrl) {
    scriptTags.push({
      properties: {
        innerHTML: `
        !function (a, b, c, e, f,) {
          var d = a.createElement("script");
          d.id = "wcw_socket_script", d.src = b + "/js/WCWSocket2.js?cid=" + c + "&uri=" + encodeURIComponent(a.location) + "&native=" + e + "&language=" + f, document.head.appendChild(d)
        }(document, '${$awMergedConfig.chatbotUrl}', '${$awMergedConfig.chatbotId}', true, "HU");
        window.addEventListener("message", function (e) {
          if (e.data === "closeWebview") {
            window.tabWebviewClose();
          }
        }, false);`,
      },
      attributes: {},
    });
  }

  if ($awMergedConfig.optimonkAccountId) {
    scriptTags.push({
      properties: {
        src: `https://onsite.optimonk.com/script.js?account=${$awMergedConfig.optimonkAccountId}`,
        async: true,
      },
      attributes: {},
    });
  }

  const searchParams = new URLSearchParams(location.search);
  const isDelayable =
    searchParams.get('delayThirdParty') != null &&
    ['', '1', 'true'].includes(searchParams.get('delayThirdParty') || '') &&
    !Number.isNaN($awMergedConfig.thirdPartyScriptDelayMs);

  if (isDelayable) {
    setTimeout(() => {
      insertScripts(scriptTags);
    }, $awMergedConfig.thirdPartyScriptDelayMs);
  } else {
    insertScripts(scriptTags);
  }

  function insertScripts (scriptsToInsert: ScriptTagType[]) {
    const df = new DocumentFragment();
    for (const script of scriptsToInsert) {
      try {
        const scriptElement = document.createElement('script');
        for (const p in script.properties) {
          const key = p as keyof typeof script.properties;
          const value = script.properties[key];
          if (value !== undefined) {
            (scriptElement as unknown as Record<string, unknown>)[p] = value;
          }
        }
        for (const a in script.attributes) {
          scriptElement.setAttribute(a, String(script.attributes[a]));
        }
        df.appendChild(scriptElement);
      } catch (err) {
        $logger.error(err);
      }
    }
    document.head.appendChild(df);
  }

  return {
    oneTrustScripts,
    insertScripts,
  };
}

export type ThirdPartyScriptsPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});
